<script setup lang="ts">
import { ref, watch } from 'vue'

interface Props {
  /** Spotify cover URL; absent for playlists and tracks without artwork. */
  src?: string | undefined
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), { src: undefined, size: 'md' })

/**
 * Spotify cover URLs expire, so a 404 is a normal outcome rather than a bug.
 * The failed image is dropped for the placeholder instead of leaving a broken
 * icon that changes the box's size and breaks the row it sits in.
 */
const failed = ref(false)
watch(
  () => props.src,
  () => {
    failed.value = false
  },
)
</script>

<template>
  <span class="cover" :class="`cover--${props.size}`" aria-hidden="true">
    <img v-if="props.src && !failed" class="cover__img" :src="props.src" alt="" @error="failed = true" />
    <span v-else class="cover__fallback">♪</span>
  </span>
</template>

<style scoped>
/* The class inventory has no cover thumbnail, and styles/ is off limits here,
   so the box lives with the component. Values are tokens only. */
.cover {
  flex: 0 0 auto;
  display: block;
  overflow: hidden;
  border-radius: var(--radius-md);
  background-color: rgb(0 0 0 / 35%);
  border: 1px solid var(--color-border);
}

/* Table rows and sidebar entries: a swatch, not artwork. */
.cover--xs {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
}

.cover--sm {
  width: 48px;
  height: 48px;
}

.cover--md {
  width: 64px;
  height: 64px;
}

.cover--lg {
  width: 96px;
  height: 96px;
}

.cover__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover__fallback {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: var(--color-text-faint);
  font-size: var(--text-lg);
}
</style>
